<template>
  <div class="nps-survey">
    <div class="nps-survey__container">
      <CloseIcon :class="'nps-survey__close'" @click="closeSurvey" v-if="step === 'rating' || step === 'review'" />

      <div class="nps-survey__rating-step" v-if="step === 'rating'">
        <h3 class="nps-survey__headline">
          How likely are you to recommend SQL Habit to a friend or colleague?
        </h3>

        <div class="nps-survey__ratings">
          <div class="nps-survey__rating" v-for="rating in ratings" v-bind:key="rating" @click="submitRating(rating)">
            {{ rating }}
          </div>
        </div>

        <div class="nps-survey__labels">
          <span>Not likely at all</span>
          <span>Extremely likely</span>
        </div>
      </div>

      <div class="nps-survey__feedback-step" v-if="step === 'feedback'">
        <img class="nps-survey__feedback-form-avatar" v-bind:src="require('@images/avatars/peace_half.png')" />

        <div class="nps-survey__feedback-form-inputs">
          <textarea class="nps-survey__feedback" v-model="feedback" placeholder="Hi, it's Anatoli, the author of SQL Habit. I'd love to hear your feedback! What can I do better? What do you want to see in the course?"></textarea>

          <div class="nps-survey__feedback-form-ctas">
            <a href="#" class="nps-survey__skip-feedback" @click="closeSurvey">Skip</a>
            <button class="nps-survey__submit-feedback btn" @click="submitFeedback">Submit feedback</button>
          </div>
        </div>
      </div>

      <div class="nps-survey__review-step" v-if="step === 'review'">
        <h3 class="nps-survey__headline">
          Thanks a lot, I'm so glad you're enjoying the course!
        </h3>

        <div class="nps-survey__review">
          I'd really appreciate if you take a minute and leave a review for SQL Habit. <a href="/reviews/new">Click here</a> to visit the review page.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { notify } from "modules/notify";
import CloseIcon from "@images/font_awesome/x-light.svg";
import trackEvent from "modules/track-event";
import Sentry from "modules/sentry";

export default {
  data() {
    return {
      feedback: null,
      rating: null,
      step: "rating",
      ratings: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
    }
  },
  methods: {
    closeSurvey() {
      this.$destroy();

      if(this.$el.parentNode) {
        this.$el.parentNode.removeChild(this.$el);
      }
    },
    submitRating(rating) {
      let formData = new FormData();

      formData.append("nps_score[rating]", rating);

      axios.patch("/api/nps_scores", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
        }
      }).then(response => {
        this.rating = rating;

        if(rating > 8) {
          this.step = "review";
        } else {
          this.step = "feedback";
        }

        trackEvent("GAEvent", {
          category: "nps_survey",
          action: "rating"
        });
      }).catch(function (error) {
        notify({
          message: "Smth went wrong",
          type: "error"
        });

        Sentry.captureException(error);
      });
    },
    submitFeedback() {
      let formData = new FormData();

      formData.append("nps_score[feedback]", this.feedback);

      axios.patch("/api/nps_scores", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
        }
      }).then(response => {
        this.closeSurvey();

        notify({
          message: "Thanks a lot for filling the survey!"
        });
      }).catch(function (error) {
        notify({
          message: "Smth went wrong",
          type: "error"
        });

        Sentry.captureException(error);
      });
    }
  },
  components: {
    CloseIcon
  }
}
</script>

<style lang="scss">
.nps-survey {
  position: fixed;
  bottom: $px24;
  left: 50%;
  transform: translateX(-50%);
  max-width: 720px;
  padding: $px24;
  z-index: 999999;

  border-radius: $px4;
  box-shadow: 0 $px4 $px16 0 $grey-8;

  background-color: $grey-10;

  &__close {
    position: absolute;
    top: $px4;
    right: $px4;
    width: $px24;
    height: $px24;

    cursor: pointer;
  }

  &__headline {
    margin: 0;
    margin-bottom: $px24;
  }

  &__ratings {
    display: flex;
    justify-content: space-between;
    margin-bottom: $px4;
  }

  &__rating {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: $px16;
    width: $px48;

    border: $px1 solid $grey-9;
    border-radius: $px4;

    @include font-large;
    text-align: center;
    line-height: 1;

    cursor: pointer;

    &:not(:last-child) {
      margin-right: $px4;
    }

    transition: all .2s ease-in-out;

    &:hover {
      background-color: $grey-9;

      transform: scale(1.1);
    }
  }

  &__labels {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__feedback {
    width: 100%;
    height: $px96;
    padding: $px8;

    border: $px2 solid $grey-7;
    border-radius: $px4;
    outline: none;

    @include font-regular;

    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    &::placeholder {
      @include font-regular;
      color: $grey-7;
    }
  }

  &__feedback-step {
    padding-left: $px144;
  }

  &__feedback-form {
    display: flex;
  }

  &__feedback-form-inputs {
    width: 100%;
    min-width: $px496;
    padding-left: $px16;
  }

  &__feedback-form-avatar {
    width: 160px;
    height: auto;
    position: absolute;
    bottom: 0;
    left: $px16;
  }

  &__feedback-form-ctas {
    margin-top: $px8;

    text-align: right;
  }

  &__skip-feedback {
    margin-right: $px8;

    color: $grey-7;
    text-decoration: none;
  }

  &__review-step {
    width: 672px;
  }
}
</style>
